import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Menu, X } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import LanguageSwitcher from './LanguageSwitcher';

const Header = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const navigate = useNavigate();
  const { isAuthenticated, user } = useAuth();
  const { t } = useLanguage();

  // Handle scroll effect
  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Close mobile menu on resize
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 768) {
        setMobileMenuOpen(false);
      }
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Prevent body scroll when mobile menu is open
  useEffect(() => {
    if (mobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [mobileMenuOpen]);

  const navLinks = [
    { label: t('nav.howItWorks'), href: '#how-it-works' },
    { label: t('nav.accountPlans'), href: '#plans' },
    { label: t('nav.tradingRules'), href: '#rules' },
    { label: t('nav.dashboard'), href: '/login' },
    { label: t('nav.faq'), href: '#faq' }
  ];

  const handleNavClick = (href) => {
    if (href.startsWith('#')) {
      const element = document.querySelector(href);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    } else {
      navigate(href);
    }
    setMobileMenuOpen(false);
  };

  return (
    <>
      <header style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        zIndex: 1000,
        background: scrolled ? 'rgba(11, 11, 11, 0.98)' : 'rgba(11, 11, 11, 0.9)',
        backdropFilter: 'blur(12px)',
        borderBottom: '1px solid rgba(255, 255, 255, 0.08)',
        transition: 'all 0.3s ease'
      }}>
        <nav style={{
          maxWidth: '1400px',
          margin: '0 auto',
          padding: '12px 20px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between'
        }}>
          {/* Logo */}
          <div 
            onClick={() => navigate('/')}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '10px',
              cursor: 'pointer',
              flexShrink: 0
            }}
          >
            <img 
              src="/logo.png" 
              alt="Prop Indexa" 
              className="header-logo"
              style={{ 
                height: '40px',
                width: 'auto'
              }} 
            />
            <span className="header-brand-name" style={{
              fontSize: '20px',
              fontWeight: 800,
              color: 'var(--accent-gold)',
              letterSpacing: '0.05em',
              textTransform: 'uppercase'
            }}>
              PROP INDEXA
            </span>
          </div>

          {/* Desktop Navigation */}
          <div className="desktop-nav" style={{
            display: 'flex',
            alignItems: 'center',
            gap: '28px'
          }}>
            {navLinks.map((link) => (
              <button
                key={link.label}
                onClick={() => handleNavClick(link.href)}
                style={{
                  background: 'none',
                  border: 'none',
                  color: 'var(--text-secondary)',
                  textDecoration: 'none',
                  fontSize: '14px',
                  fontWeight: 500,
                  transition: 'color 0.2s ease',
                  cursor: 'pointer',
                  whiteSpace: 'nowrap'
                }}
                onMouseEnter={(e) => e.target.style.color = 'var(--accent-gold)'}
                onMouseLeave={(e) => e.target.style.color = 'var(--text-secondary)'}
              >
                {link.label}
              </button>
            ))}
          </div>

          {/* Desktop Actions */}
          <div className="desktop-actions" style={{
            display: 'flex',
            alignItems: 'center',
            gap: '12px'
          }}>
            {/* Language Selector */}
            <LanguageSwitcher variant="header" />

            <Button
              onClick={() => navigate(isAuthenticated ? '/dashboard' : '/login')}
              variant="outline"
              style={{
                padding: '8px 16px',
                fontSize: '14px',
                border: '1px solid var(--border-primary)',
                background: 'transparent',
                color: 'var(--text-primary)'
              }}
            >
              {isAuthenticated ? `Hi, ${user?.full_name?.split(' ')[0]}` : t('nav.login')}
            </Button>
            <Button
              onClick={() => handleNavClick('#plans')}
              className="btn-primary"
              style={{
                padding: '8px 20px',
                fontSize: '14px'
              }}
            >
              {t('nav.getFunded')}
            </Button>
          </div>

          {/* Mobile Menu Button - Hamburger Icon */}
          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="mobile-menu-btn"
            style={{
              display: 'none',
              background: 'transparent',
              border: '1px solid var(--border-primary)',
              borderRadius: '8px',
              padding: '8px',
              color: 'var(--text-primary)',
              cursor: 'pointer',
              transition: 'all 0.2s ease'
            }}
            aria-label="Toggle menu"
          >
            {mobileMenuOpen ? <X size={22} /> : <Menu size={22} />}
          </button>
        </nav>
      </header>

      {/* Mobile Menu Overlay */}
      <div 
        className={`mobile-menu-overlay ${mobileMenuOpen ? 'open' : ''}`}
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.8)',
          zIndex: 998,
          opacity: mobileMenuOpen ? 1 : 0,
          visibility: mobileMenuOpen ? 'visible' : 'hidden',
          transition: 'all 0.3s ease'
        }}
        onClick={() => setMobileMenuOpen(false)}
      />

      {/* Mobile Menu Panel */}
      <div 
        className={`mobile-menu-panel ${mobileMenuOpen ? 'open' : ''}`}
        style={{
          position: 'fixed',
          top: 0,
          right: 0,
          width: '85%',
          maxWidth: '320px',
          height: '100vh',
          background: 'var(--bg-secondary)',
          zIndex: 999,
          transform: mobileMenuOpen ? 'translateX(0)' : 'translateX(100%)',
          transition: 'transform 0.3s ease',
          display: 'flex',
          flexDirection: 'column',
          boxShadow: '-10px 0 40px rgba(0, 0, 0, 0.5)'
        }}
      >
        {/* Mobile Menu Header */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '16px 20px',
          borderBottom: '1px solid var(--border-subtle)'
        }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '10px'
          }}>
            <img src="/logo.png" alt="Prop Indexa" style={{ height: '32px' }} />
            <span style={{
              fontSize: '16px',
              fontWeight: 800,
              color: 'var(--accent-gold)',
              letterSpacing: '0.05em'
            }}>
              PROP INDEXA
            </span>
          </div>
          <button
            onClick={() => setMobileMenuOpen(false)}
            style={{
              background: 'transparent',
              border: 'none',
              color: 'var(--text-muted)',
              cursor: 'pointer',
              padding: '4px'
            }}
          >
            <X size={24} />
          </button>
        </div>

        {/* Mobile Menu Links */}
        <div style={{
          flex: 1,
          padding: '20px',
          overflowY: 'auto'
        }}>
          {navLinks.map((link, index) => (
            <button
              key={link.label}
              onClick={() => handleNavClick(link.href)}
              style={{
                width: '100%',
                display: 'flex',
                alignItems: 'center',
                padding: '16px',
                background: 'transparent',
                border: 'none',
                borderBottom: '1px solid var(--border-subtle)',
                color: 'var(--text-primary)',
                fontSize: '16px',
                fontWeight: 500,
                cursor: 'pointer',
                textAlign: 'left',
                transition: 'all 0.2s ease'
              }}
            >
              {link.label}
            </button>
          ))}
        </div>

        {/* Mobile Menu Footer */}
        <div style={{
          padding: '20px',
          borderTop: '1px solid var(--border-subtle)',
          display: 'flex',
          flexDirection: 'column',
          gap: '12px'
        }}>
          {/* Language Switcher for Mobile */}
          <div style={{ marginBottom: '8px' }}>
            <LanguageSwitcher variant="header" />
          </div>
          <Button
            onClick={() => {
              navigate(isAuthenticated ? '/dashboard' : '/login');
              setMobileMenuOpen(false);
            }}
            variant="outline"
            style={{
              width: '100%',
              padding: '14px',
              fontSize: '15px',
              border: '1px solid var(--border-primary)',
              background: 'transparent',
              color: 'var(--text-primary)'
            }}
          >
            {isAuthenticated ? t('nav.dashboard') : t('nav.login')}
          </Button>
          <Button
            onClick={() => {
              handleNavClick('#plans');
            }}
            className="btn-primary"
            style={{
              width: '100%',
              padding: '14px',
              fontSize: '15px'
            }}
          >
            {t('nav.getFunded')}
          </Button>
        </div>
      </div>

      {/* Responsive Styles */}
      <style>{`
        .desktop-nav,
        .desktop-actions {
          display: flex;
        }
        
        .mobile-menu-btn {
          display: none !important;
        }
        
        .header-logo {
          height: 40px;
        }
        
        .header-brand-name {
          font-size: 20px;
        }
        
        @media (max-width: 1024px) {
          .desktop-nav {
            display: none !important;
          }
          
          .desktop-actions {
            display: none !important;
          }
          
          .mobile-menu-btn {
            display: flex !important;
          }
        }
        
        @media (max-width: 480px) {
          .header-logo {
            height: 32px !important;
          }
          
          .header-brand-name {
            font-size: 16px !important;
          }
        }
      `}</style>
    </>
  );
};

export default Header;
